import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchActiveCountries } from '@/shared/api/places';
import { toOptions } from '@/shared/lib/options';
import { useSaveForm } from '@/shared/lib/useSaveForm';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import { fetchRegion, saveRegion, type Region } from './api';

type Props = {
  /** Edit when set, create otherwise. */
  id?: number;
  onClose: () => void;
};

const RegionForm = ({ region, onClose }: { region?: Region; onClose: () => void }) => {
  const qc = useQueryClient();
  const [name, setName] = useState(region?.name ?? '');
  const [countryId, setCountryId] = useState(region?.country_id ? String(region.country_id) : '');

  const countries = useQuery({ queryKey: ['countries-active'], queryFn: fetchActiveCountries });

  const { errors, clearErrors, save, isPending } = useSaveForm(saveRegion, () => {
    void qc.invalidateQueries({ queryKey: ['place-regions'] });
    onClose();
  });

  return (
    <ModalDialog
      onClose={onClose}
      isSaving={isPending}
      onSave={() => save({ id: region?.id, name, countryId })}
    >
      <FormField label="Страна" errors={errors.country_id}>
        <FilterSelect
          options={toOptions(countries.data)}
          value={countryId}
          isLoading={countries.isLoading}
          onChange={(value) => {
            setCountryId(value);
            clearErrors('country_id');
          }}
        />
      </FormField>
      <FormField label="Название" errors={errors.name}>
        <input
          className="form-control input-sm"
          style={{ width: 250 }}
          placeholder="Название..."
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearErrors('name');
          }}
        />
      </FormField>
      {errors.common && <p className="text-red">{errors.common[0]}</p>}
    </ModalDialog>
  );
};

export const RegionModal = ({ id, onClose }: Props) => {
  const { data: region, isLoading } = useQuery({
    queryKey: ['place-region', id],
    queryFn: () => fetchRegion(id as number),
    enabled: id !== undefined,
    gcTime: 0, // always edit fresh data
  });

  if (id !== undefined && isLoading) {
    return (
      <ModalDialog onClose={onClose}>
        <p>Загрузка…</p>
      </ModalDialog>
    );
  }
  return <RegionForm region={region} onClose={onClose} />;
};
